import React, { useState } from 'react';
import { RightOutlined } from '@ant-design/icons';
import type { CreatorProductCategory } from '../types/category';

type CategorySelectorProps = {
  categories: CreatorProductCategory[];
  value: string;
  onChange: (category: CreatorProductCategory | null) => void;
};

const pillActiveClass =
  'rounded-full bg-[linear-gradient(rgb(36,175,221),rgb(59,77,215))] px-2.5 text-sm leading-[26px] text-white';

const pillIdleClass =
  'rounded-full border-0 bg-transparent px-2.5 text-sm leading-[26px] text-slate-600 hover:text-[#596ae8]';

export function categoryLabel(node: CreatorProductCategory) {
  return node.categoryName?.trim() || node.nameSlug?.trim() || node.categoryId?.trim() || '';
}

function visibleChildren(node: CreatorProductCategory) {
  return (node.children ?? []).filter((item) => item.categoryId?.trim() && categoryLabel(item));
}

function containsCategory(node: CreatorProductCategory, categoryId: string): boolean {
  if (node.categoryId?.trim() === categoryId) return true;
  return visibleChildren(node).some((child) => containsCategory(child, categoryId));
}

function FlyoutColumns({
  nodes,
  selectedId,
  onSelect
}: {
  nodes: CreatorProductCategory[];
  selectedId: string;
  onSelect: (category: CreatorProductCategory) => void;
}) {
  const [hoverId, setHoverId] = useState('');
  const hovered = nodes.find((node) => node.categoryId?.trim() === hoverId);
  const next = hovered ? visibleChildren(hovered) : [];

  return (
    <div className="flex">
      <div className="max-h-[180px] min-w-[140px] overflow-y-auto">
        {nodes.map((node) => {
          const id = node.categoryId?.trim() ?? '';
          const active = id === hoverId || id === selectedId;
          const hasChildren = visibleChildren(node).length > 0;
          return (
            <button
              key={id}
              type="button"
              className={`flex w-full items-center whitespace-nowrap border-0 bg-transparent px-3 text-left text-sm leading-8 ${
                active ? 'bg-[#3b4dd7]/20 text-[#3b4dd7]' : 'text-slate-700 hover:bg-[#3b4dd7]/20 hover:text-[#3b4dd7]'
              }`}
              onMouseEnter={() => setHoverId(id)}
              onClick={() => onSelect(node)}
            >
              <span className="flex-1">{categoryLabel(node)}</span>
              {hasChildren ? <RightOutlined className="ml-3 shrink-0 text-[10px]" /> : null}
            </button>
          );
        })}
      </div>
      {next.length > 0 ? (
        <div className="border-l border-[#eef0f4]">
          <FlyoutColumns nodes={next} selectedId={selectedId} onSelect={onSelect} />
        </div>
      ) : null}
    </div>
  );
}

function CategoryPill({
  category,
  selectedId,
  onSelect
}: {
  category: CreatorProductCategory;
  selectedId: string;
  onSelect: (category: CreatorProductCategory) => void;
}) {
  const children = visibleChildren(category);
  const selected = containsCategory(category, selectedId);

  return (
    <div className="group relative mb-2 mr-1">
      <button
        type="button"
        className={`cursor-pointer border-0 ${selected ? pillActiveClass : pillIdleClass}`}
        onClick={() => onSelect(category)}
      >
        {categoryLabel(category)}
      </button>
      {children.length > 0 ? (
        <div className="invisible absolute left-0 top-full z-20 pt-1 opacity-0 transition group-hover:visible group-hover:opacity-100">
          <div className="rounded-lg bg-white p-1 shadow-lg">
            <FlyoutColumns nodes={children} selectedId={selectedId} onSelect={onSelect} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function CategorySelector({ categories, value, onChange }: CategorySelectorProps) {
  const roots = categories.filter((item) => item.categoryId?.trim() && categoryLabel(item));

  return (
    <div className="flex min-w-0 flex-1 flex-wrap">
      {roots.map((category) => (
        <CategoryPill
          key={category.categoryId}
          category={category}
          selectedId={value}
          onSelect={(next) => onChange(next.categoryId?.trim() === value ? null : next)}
        />
      ))}
    </div>
  );
}
